import { fontFamily } from 'tailwindcss/defaultTheme';

import {
    createSpacing,
    createWidth,
    extendColours
} from './lib/tailwind/helpers.js';

import {
    expandInteractions,
    typeStyles
} from './lib/tailwind/plugins.js';

/**
 * @type {import('tailwindcss').Config}
 */
export default {
    content: [
        './src/components/**/*.jsx',
        './src/server/templates/pages/main.handlebars'
    ],
    future: {
        hoverOnlyWhenSupported: true
    },
    theme: {
        screens: {
            'sm': '768px', // tablet
            'md': '1024px', // desktop
            'lg': '1440px' // large desktop
        },
        colors: {},
        fontFamily: {
            'sans': ['"Saans"', ...fontFamily.sans],
            'serif': ['"TWK Ghost"', ...fontFamily.serif]
        },
        spacing: {
            '0': '0',
            'layout-outer': 'var(--layout-outer-padding)',
            'layout-col': 'var(--layout-col-padding)',
            ...createSpacing(1),
            ...createSpacing(2),
            ...createSpacing(4),
            ...createSpacing(5),
            ...createSpacing(6),
            ...createSpacing(7),
            ...createSpacing(8),
            ...createSpacing(10),
            ...createSpacing(12),
            ...createSpacing(13),
            ...createSpacing(16),
            ...createSpacing(18),
            ...createSpacing(20),
            ...createSpacing(24),
            ...createSpacing(28),
            ...createSpacing(30),
            ...createSpacing(32),
            ...createSpacing(35),
            ...createSpacing(40),
            ...createSpacing(42),
            ...createSpacing(44),
            ...createSpacing(45),
            ...createSpacing(48),
            ...createSpacing(56),
            ...createSpacing(60),
            ...createSpacing(64),
            ...createSpacing(72),
            ...createSpacing(80),
            ...createSpacing(88),
            ...createSpacing(96),
            ...createSpacing(108),
            ...createSpacing(116),
            ...createSpacing(124),
            ...createSpacing(160),
            ...createWidth(6),
            ...createWidth(8),
            ...createWidth(11)
        },
        typeStyles: ({ theme }) => ({
            '1': {
                displayName: 'Main/H1',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 38,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                md: {
                    fontSize: 48
                }
            },
            '2': {
                displayName: 'Main/H2',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 26,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                letterSpacing: 0.52,
                md: {
                    fontSize: 34,
                    letterSpacing: 0.68
                }
            },
            '3': {
                displayName: 'Main/H3',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 22,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '150%',
                md: {
                    fontSize: 25
                }
            },
            '4': {
                displayName: 'Main/H4',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 18,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '150%',
                md: {
                    fontSize: 20
                }
            },
            '5': {
                displayName: 'Main/H5',
                fontFamily: theme('fontFamily.serif'),
                fontFeatureSettings: '\'salt\' on',
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                letterSpacing: 0.64,
                md: {
                    fontSize: 18,
                    letterSpacing: 0.72,
                }
            },
            'body-l': {
                displayName: 'Main/Body L',
                fontFamily: theme('fontFamily.sans'),
                fontFeatureSettings: '\'salt\' on',
                fontSize: 18,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '140%',
                letterSpacing: 0.72,
                md: {
                    fontSize: 20,
                    letterSpacing: 0.8
                }
            },
            'body-r': {
                displayName: 'Main/Body R',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
                letterSpacing: 0.64
            },
            'caption': {
                displayName: 'Main/Caption',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 10,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
                letterSpacing: 0.4,
                md: {
                    fontSize: 12,
                    letterSpacing: 0.48
                }
            },
            'eyebrow': {
                displayName: 'Main/Eyebrow',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
                letterSpacing: 1.2,
                textTransform: 'uppercase'
            },
            'typed-input': {
                displayName: 'Main/Typed Input',
                fontFamily: theme('fontFamily.sans'),
                fontStyle: 'normal',
                fontWeight: 380,
                fontSize: 16,
                lineHeight: '120%',
                letterSpacing: 0.64,
                md: {
                    fontSize: 13,
                    lineHeight: '150%',
                    letterSpacing: 0.52,
                }
            },
            'input-label': {
                displayName: 'Main/Input Label',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 9,
                fontStyle: 'normal',
                fontWeight: 380,
                lineHeight: '111%',
                letterSpacing: 0.81,
                textTransform: 'uppercase'
            },
            'form-body-r': {
                displayName: 'Form/Body R',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '110%',
                letterSpacing: 0.32
            },
            'form-body-s': {
                displayName: 'Form/Body S',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '110%',
                letterSpacing: 0.64
            }
        }),
        extend: {
            borderRadius: {
                DEFAULT: '2px',
            },
            // Build smeantic colour classes from colours file
            colors: {
                transparent: 'transparent',
                ...extendColours()
            }
        }
    },
    plugins: [
        expandInteractions,
        typeStyles
    ]
};
